import React, { useEffect, useState } from 'react';
import api, { fmtDate } from '@/lib/api';
import { Loading, SectionTitle, KpiCard } from '@/components/Shared';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Star, MessageSquareText, Frown, Users, Reply, Loader2, CornerDownRight } from 'lucide-react';
import { toast } from 'sonner';

const Stars = ({ n }) => (
  <span className="flex items-center gap-0.5" aria-label={`${n} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= n ? 'text-accent fill-accent' : 'text-muted-foreground/30'}`} />)}
  </span>
);

export default function AdminReviews() {
  const [reviews, setReviews] = useState(null);
  const [filter, setFilter] = useState('all');
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get('/reviews').then((r) => setReviews(r.data)).catch(() => setReviews([])); }, []);

  const sendReply = async (id) => {
    if (!replyText.trim()) return toast.error('Write a reply first');
    setSending(true);
    try {
      const r = await api.post(`/reviews/${id}/reply`, { reply: replyText.trim() });
      setReviews((rows) => rows.map((x) => (x.id === id ? { ...x, reply: r.data.reply } : x)));
      setReplyFor(null); setReplyText('');
      toast.success('Reply sent — the customer has been notified in their portal');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not send reply'); }
    finally { setSending(false); }
  };

  if (!reviews) return <Loading />;

  const avg = reviews.length ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1) : '—';
  const unhappy = reviews.filter((r) => r.rating <= 3);
  const shown = filter === 'all' ? reviews : filter === 'unhappy' ? unhappy : reviews.filter((r) => r.rating === Number(filter));

  return (
    <div className="space-y-5" data-testid="admin-reviews-page">
      <SectionTitle>Customer Reviews</SectionTitle>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total Reviews" value={String(reviews.length)} icon={MessageSquareText} testId="kpi-total-reviews" />
        <KpiCard label="Average Rating" value={reviews.length ? `${avg}/5` : 'No reviews'} icon={Star} testId="kpi-avg-rating" />
        <KpiCard label="Unhappy (≤3★)" value={String(unhappy.length)} icon={Frown} testId="kpi-unhappy" />
        <KpiCard label="5-Star Reviews" value={String(reviews.filter((r) => r.rating === 5).length)} icon={Users} testId="kpi-five-star" />
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="all" data-testid="reviews-filter-all">All ({reviews.length})</TabsTrigger>
          <TabsTrigger value="unhappy" data-testid="reviews-filter-unhappy" className="data-[state=active]:text-destructive">Unhappy ≤3★ ({unhappy.length})</TabsTrigger>
          <TabsTrigger value="5" data-testid="reviews-filter-5">5★</TabsTrigger>
          <TabsTrigger value="4" data-testid="reviews-filter-4">4★</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="space-y-2">
        {shown.length === 0 && (
          <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="reviews-empty">
            No reviews in this view yet.
          </div>
        )}
        {shown.map((r) => (
          <div key={r.id} data-testid={`review-row-${r.id}`}
            className={`rounded-xl border bg-card p-4 ${r.rating <= 3 ? 'border-destructive/50 bg-destructive/5' : 'border-border'}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <Stars n={r.rating} />
                <span className="font-semibold text-sm">{r.name}</span>
                {r.rating <= 3 && <span className="text-[11px] font-semibold rounded-full bg-destructive/10 text-destructive px-2 py-0.5">Needs attention</span>}
              </div>
              <span className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</span>
            </div>
            {r.comment && <p className="mt-2 text-sm text-foreground/85">{r.comment}</p>}
            {r.reply ? (
              <div className="mt-3 rounded-lg bg-muted/60 border border-border p-3" data-testid={`review-reply-${r.id}`}>
                <p className="text-[11px] font-semibold text-primary flex items-center gap-1"><CornerDownRight className="h-3.5 w-3.5" />Replied by {r.reply.by} · {fmtDate(r.reply.at)}</p>
                <p className="text-sm mt-1">{r.reply.text}</p>
              </div>
            ) : replyFor === r.id ? (
              <div className="mt-3 space-y-2">
                <Textarea rows={2} value={replyText} onChange={(e) => setReplyText(e.target.value.slice(0, 500))} placeholder="Write your response to this customer..." data-testid={`review-reply-input-${r.id}`} />
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={() => sendReply(r.id)} disabled={sending} data-testid={`review-reply-send-${r.id}`}>
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Reply className="h-4 w-4 mr-1" />Send Reply</>}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setReplyFor(null); setReplyText(''); }}>Cancel</Button>
                </div>
              </div>
            ) : (
              <Button size="sm" variant="outline" className="mt-3" onClick={() => { setReplyFor(r.id); setReplyText(''); }} data-testid={`review-reply-button-${r.id}`}>
                <Reply className="h-4 w-4 mr-1" />Reply
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
